import { LOCALSTORAGE_SHOPPING_CART_ID } from '@constants/shoppingCart';
import { useAlcoholEligibilityContext } from '@context/AlcoholEligibilityContext';
import { useAuthentication } from '@context/businessContainer';
import { checkAddressSource } from '@context/shoppingCart';
import { useEnrichAlgoliaContexts } from '@hooks/businessContainer';
import { useShoppingCart, useSideCart } from '@hooks/cart/useShoppingCart';
import useI18n from '@hooks/i18n/useI18n';
import {
  ABTEST_DELIVERY_BAR,
  EVENT_SOURCE,
} from '@monofront/business/constants';
import { useEligibility, useFeatureIsOn } from '@monofront/business/contexts';
import { validateDeliveryBarState } from '@monofront/business/contexts/deliveryBar/deliveryBarFormValidator';
import {
  useDeliveryBarSwitchPickupToCourier,
  useDeliveryMethodEligibility,
  useLocalStorage,
  useUserTags,
} from '@monofront/business/hooks';
import {
  formatProductShoppingCartToTracking,
  getIsFresh,
  getIsUltraFresh,
  getMinimumProductQuantity,
} from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import {
  AddToShoppingCartContext,
  CartEventType,
  IShoppingCartProduct,
  ShoppingCartProductType,
} from '@monofront/types';
import { productService } from '@services/businessContainer';
import { isMobileApp } from '@utils/environment';
import logger from '@utils/logger';
import { sendMessageToMobile } from '@utils/mobile';
import { trackShoppingCartUpdate } from '@utils/tracking';
import { useTranslations } from 'next-intl';
import { debounce } from 'radash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import aa from 'search-insights';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import { usePreferredShippingMethodWithConfigurations } from '@hooks/usePreferredShippingMethodWithConfiguration';
import { useDeliveryBar } from '@context/DeliveryBarContext';
import { DeliveryMethodSwitchSuccess } from '@features/DeliveryBar/Toasts/DeliveryMethodSwitchSuccess';
import { useUniqueToast } from '@hooks/useUniqueToast';

interface ICount {
  oldCount?: number;
  newCount?: number;
}

const emptyContext = [];

const useProductToShoppingCart = (
  shoppingCartProduct: ShoppingCartProductType,
  containerContext: AddToShoppingCartContext,
  addToShoppingCartInsight?: () => void,
  isReplacement = false,
) => {
  const { checkNeedEligibility } = useEligibility();
  // Only used to debug fresh
  const freshAlgoliaContexts = useEnrichAlgoliaContexts(emptyContext);
  const { hasAlcoholOptIntBeenTriggered } = useAlcoholEligibilityContext();
  const tGlobal = useTranslations('Global');
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState<ICount>({});
  const { dispatchSideCart } = useSideCart();
  const {
    shoppingCart,
    shoppingCartId,
    addProductToShoppingCart,
    updateProductInShoppingCart,
    deleteProductsFromShoppingCart,
  } = useShoppingCart();
  const { currency } = useI18n();
  const { authUser, customer, isAuthenticated } = useAuthentication();
  const customerTags = useUserTags(useAuthentication);
  const [localStorageShoppingCartID] = useLocalStorage<string | null>(
    LOCALSTORAGE_SHOPPING_CART_ID,
    null,
  );
  const { resolvedCustomer } = useLocalOrRemoteCustomer();
  const uniqueToast = useUniqueToast({
    toastId: 'delivery-bar-success-toast',
  });
  const tDeliveryBarToasts = useTranslations('DeliveryBar.Toasts');

  const { deliveryBarSwitchPickupToCourier } =
    useDeliveryBarSwitchPickupToCourier({
      customer: {
        address: resolvedCustomer?.address,
        preferredShippingMethod: resolvedCustomer?.preferredShippingMethod,
      },
      shoppingCartProduct,
      onSwitchCallback: {
        success: () => {
          uniqueToast.update('delivery-bar-success-toast', {
            render: DeliveryMethodSwitchSuccess,
          });
        },
        error: () => {
          toast(tDeliveryBarToasts('shippingMethodNotAvailable'), {
            progressClassName: 'toast-error',
          });
        },
      },
    });

  const selectedShippingMethod = usePreferredShippingMethodWithConfigurations();

  const isDeliveryBarABTestEnabled = useFeatureIsOn(ABTEST_DELIVERY_BAR);
  const {
    isSidePanelOpen,
    setIsSidePanelOpen,
    setWaitingProduct,
    waitingProduct,
  } = useDeliveryBar();
  const {
    isShippingFreshMethodEligible,
    isShippingUltraFreshMethodEligible,
    isAddressFreshMethodEligible,
    isAddressUltraFreshMethodEligible,
  } = useDeliveryMethodEligibility({ useAuthentication, useLocalStorage });

  const needsCourierDelivery =
    (getIsFresh(shoppingCartProduct.tags) &&
      !isShippingFreshMethodEligible &&
      isAddressFreshMethodEligible) ||
    (getIsUltraFresh(shoppingCartProduct.tags) &&
      !isShippingUltraFreshMethodEligible &&
      isAddressUltraFreshMethodEligible);

  const canComputeEligibility = useMemo(() => {
    return Boolean(
      resolvedCustomer?.address && resolvedCustomer?.preferredShippingMethod,
    );
  }, [resolvedCustomer]);

  //Add to cart if eligible after side panel is closed
  useEffect(() => {
    if (!isSidePanelOpen && waitingProduct?.sku === shoppingCartProduct.sku) {
      const eligibilityValidation = !(
        (getIsFresh(shoppingCartProduct.tags) &&
          !isShippingFreshMethodEligible) ||
        (getIsUltraFresh(shoppingCartProduct.tags) &&
          !isShippingUltraFreshMethodEligible)
      );

      if (eligibilityValidation && canComputeEligibility) {
        incrementCount();
      }
    }
  }, [isSidePanelOpen]);

  const handleDebouncedCount = useCallback(
    debounce({ delay: 500 }, (count) => {
      if (!shoppingCartId) {
        logger.error(new Error('Unexpected empty `shoppingCartId`'), {
          isAuthenticated,
          customer: !!Object.keys(customer ?? {}).length,
          localStorageShoppingCartID,
        });
        toast(tGlobal('errorOccurredTryAgain'), {
          progressClassName: 'toast-error',
        });

        return;
      }

      if (
        Number.isInteger(count.oldCount) &&
        Number.isInteger(count.newCount)
      ) {
        editShoppingCart(shoppingCartId, count.oldCount, count.newCount);
      }
    }),
    [shoppingCartId, shoppingCart.products],
  );

  const currentProductQuantity: number =
    shoppingCart.products?.find(
      (product: IShoppingCartProduct) =>
        product.objectID === shoppingCartProduct.objectID,
    )?.quantity || 0;

  const minimumQuantity = getMinimumProductQuantity(shoppingCartProduct.tags);

  const checkDeliveryBarDataValidity = useCallback(() => {
    return validateDeliveryBarState({
      selectedShippingMethod: selectedShippingMethod ?? null,
      shoppingCart,
      isShippingFreshMethodEligible,
      isShippingUltraFreshMethodEligible,
    });
  }, [
    selectedShippingMethod,
    shoppingCart,
    isShippingFreshMethodEligible,
    isShippingUltraFreshMethodEligible,
  ]);

  useEffect(() => {
    if (isDeliveryBarABTestEnabled) {
      return;
    }

    if (
      (getIsFresh(shoppingCartProduct.tags) &&
        !customerTags?.includes('fresh-user')) ||
      (getIsUltraFresh(shoppingCartProduct.tags) &&
        !customerTags?.includes('ultra-fresh-user'))
    ) {
      logger.warn('Non-eligible user has seen a gated product!', {
        shoppingCartId,
        product: shoppingCartProduct,
        customer,
        user_id: authUser?.uid,
        freshAlgoliaContexts,
        containerContext,
      });
    }
  }, [shoppingCartProduct]);

  const checkBeforeIncrement = useCallback(() => {
    const canIncrement = true;
    const cannotIncrement = false;

    if (!canComputeEligibility) {
      setIsSidePanelOpen(true);
      setWaitingProduct(shoppingCartProduct);

      trackEvent({
        type: 'LFDisplayDeliveryBar',
        data: { screen: 'address_bar' },
      });

      return cannotIncrement;
    }

    const isIneligibleForAlcoholProducts = hasAlcoholOptIntBeenTriggered(
      shoppingCartProduct?.product_subtype ?? '',
      () => undefined,
    );

    if (isIneligibleForAlcoholProducts) {
      return cannotIncrement;
    }
    if (!checkDeliveryBarDataValidity().isValid) {
      // if user's address is not eligible to FUF display error, else switch to courier
      if (
        (getIsFresh(shoppingCartProduct.tags) &&
          !isAddressFreshMethodEligible) ||
        (getIsUltraFresh(shoppingCartProduct.tags) &&
          !isAddressUltraFreshMethodEligible)
      ) {
        // if  (address is eligible) then the issue is the shipping method (pickup)
        setWaitingProduct(shoppingCartProduct);

        return cannotIncrement;
      }
    }

    return canIncrement;
  }, [
    canComputeEligibility,
    shoppingCartProduct,
    isAddressFreshMethodEligible,
    isAddressUltraFreshMethodEligible,
  ]);

  const checkFreshBeforeIncrement = () => {
    const canIncrement = true;
    const cannotIncrement = false;

    const needCheckEligibility = checkNeedEligibility(shoppingCartProduct);

    if (needCheckEligibility) {
      if (isMobileApp) {
        sendMessageToMobile('goToFresh');
      } else {
        dispatchSideCart({
          type: 'checkAddress',
          addFreshProductToCart: () => {
            _incrementCount();
          },
          source: getIsFresh(shoppingCartProduct.tags)
            ? checkAddressSource.FRESH_ADD_TO_CART
            : checkAddressSource.ULTRA_FRESH_ADD_TO_CART,
        });
      }

      return cannotIncrement;
    }

    return canIncrement;
  };

  const handleEditCount = async (oldCount: number, newCount: number) => {
    if (oldCount < newCount) {
      const canIncrement = isDeliveryBarABTestEnabled
        ? checkBeforeIncrement()
        : checkFreshBeforeIncrement();

      if (!canIncrement) {
        return Promise.resolve(false);
      }
    }
    setCount({ oldCount, newCount });
    await handleDebouncedCount({ oldCount, newCount });

    return Promise.resolve(true);
  };

  useEffect(() => {
    if (
      Number.isInteger(currentProductQuantity) &&
      currentProductQuantity !== count.newCount
    ) {
      setCount((prev) => ({
        oldCount: prev.newCount,
        newCount: currentProductQuantity,
      }));
    }
  }, [currentProductQuantity]);

  const _incrementCount = () => {
    setCount((prev) => {
      const next = {
        oldCount: prev?.newCount,
        newCount:
          (prev?.newCount || 0) +
          (!prev?.newCount && minimumQuantity ? minimumQuantity : 1),
      };

      handleDebouncedCount(next);

      return next;
    });
  };

  const incrementCount = () => {
    const canIncrement = isDeliveryBarABTestEnabled
      ? checkBeforeIncrement()
      : checkFreshBeforeIncrement();

    if (!canIncrement) {
      return;
    }

    _incrementCount();

    if (needsCourierDelivery) {
      deliveryBarSwitchPickupToCourier();
    }
  };

  const decrementCount = () => {
    setCount((prev) => {
      const next = {
        oldCount: prev?.newCount,
        newCount:
          (prev?.newCount || 0) -
          (prev?.newCount === minimumQuantity ? minimumQuantity : 1),
      };

      handleDebouncedCount(next);

      return next;
    });
  };

  const editShoppingCart = async (
    shoppingCartId: string,
    oldCount: number,
    newCount: number,
  ) => {
    setIsLoading(true);

    let cartEventType: CartEventType = 'addToCart';
    const quantityProcessed = Math.abs(newCount - currentProductQuantity);

    try {
      if (oldCount === newCount) {
        setIsLoading(false);

        return;
      }
      if (oldCount === 0 && newCount > 0) {
        if (
          (getIsFresh(shoppingCartProduct.tags) &&
            !customer?.omsTags?.includes('fresh-user')) ||
          (getIsUltraFresh(shoppingCartProduct.tags) &&
            !customer?.omsTags?.includes('ultra-fresh-user'))
        ) {
          logger.warn('Non-eligible user added a gated product!', {
            shoppingCartId,
            product: shoppingCartProduct,
            customer,
            user_id: authUser?.uid,
            freshAlgoliaContexts,
            containerContext,
          });
        }

        await addProductToShoppingCart(shoppingCartId, {
          ...shoppingCartProduct,
          quantity: newCount,
        });

        addToShoppingCartInsight
          ? addToShoppingCartInsight()
          : aa('convertedObjectIDs', {
              eventName: 'Product Added to Cart',
              objectIDs: [shoppingCartProduct.objectID],
              index: productService.getProductPageIndex(),
            });
        setIsLoading(false);
      } else if (newCount <= 0) {
        await deleteProductsFromShoppingCart(shoppingCartId, [
          shoppingCartProduct,
        ]);

        cartEventType = 'removeFromCart';

        setIsLoading(false);
      } else {
        if (oldCount > newCount) {
          cartEventType = 'removeFromCart';
        }

        await updateProductInShoppingCart(shoppingCartId, {
          ...shoppingCartProduct,
          quantity: newCount,
        });

        setIsLoading(false);
      }

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      trackShoppingCartUpdate(
        cartEventType,
        [
          {
            ...shoppingCartProduct,
            quantity: quantityProcessed,
          },
        ],
        containerContext,
        undefined,
      );

      const LFCartEventType = {
        addToCart: 'LFAddToCart' as const,
        removeFromCart: 'LFRemoveFromCart' as const,
      };

      trackEvent({
        type: LFCartEventType[cartEventType],
        data: {
          eventSource: isMobileApp ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          products: [
            formatProductShoppingCartToTracking(
              shoppingCartProduct,
              quantityProcessed,
            ),
          ],
          context: containerContext,
          currency,
          is_substitute: isReplacement,
        },
      });
    } catch (error: any) {
      setIsLoading(false);
      throw error;
    }
  };

  return {
    currentProductQuantity,
    localProductQuantity: count.newCount,
    handleEditCount,
    isLoading,
    incrementCount,
    decrementCount,
  };
};

export default useProductToShoppingCart;
